import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Trash2, Loader2, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fullSrc = (src) => (src.startsWith("/api") ? `${BACKEND}${src}` : src);

const inputCls =
  "w-full bg-black border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-[#A6A6B0]/50 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#6D00FF] focus:ring-1 focus:ring-[#6D00FF]/60";

const EMPTY = { name: "", category: "", desc: "" };

export const AdminWork = () => {
  const [pieces, setPieces] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/work`, { withCredentials: true });
    setPieces(data);
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, category: p.category || "", desc: p.desc || "" });
    setFile(null);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(
          `${API}/work/${editing}`,
          { name: form.name, category: form.category || null, desc: form.desc || null },
          { withCredentials: true }
        );
        toast.success("Project updated");
      } else {
        if (!file) {
          toast.error("Choose a project image");
          setSaving(false);
          return;
        }
        const fd = new FormData();
        fd.append("name", form.name);
        fd.append("category", form.category);
        fd.append("desc", form.desc);
        fd.append("file", file);
        await axios.post(`${API}/work`, fd, { withCredentials: true });
        toast.success("Project added to Work page");
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
      setFile(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save project");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this project from the Work page?")) return;
    await axios.delete(`${API}/work/${id}`, { withCredentials: true });
    toast.success("Project removed");
    load();
  };

  if (!pieces) return <p className="text-sm text-[#A6A6B0]">Loading work gallery…</p>;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">
            Work Gallery
          </h1>
          <p className="text-sm text-[#A6A6B0] mt-2">
            {pieces.length} projects live on your public Work page (and the home page).
          </p>
        </div>
        <button
          data-testid="work-add-button"
          onClick={openNew}
          className="flex items-center gap-2 bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-xs font-bold uppercase tracking-[0.14em] px-5 py-3 rounded-md transition-colors duration-300"
        >
          <ImagePlus className="w-4 h-4" /> Add Project
        </button>
      </div>

      {showForm && (
        <form data-testid="work-form" onSubmit={save} className="glass-card rounded-lg p-6 sm:p-8 mb-10 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight">
              {editing ? "Edit Project" : "New Project"}
            </h2>
            <button type="button" onClick={() => setShowForm(false)} aria-label="Close form">
              <X className="w-5 h-5 text-[#A6A6B0] hover:text-white" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="work-name-input" required value={form.name} onChange={set("name")} placeholder="Project name *" className={inputCls} />
            <input data-testid="work-category-input" value={form.category} onChange={set("category")} placeholder="Category (e.g. AI Campaign)" className={inputCls} />
          </div>
          <textarea data-testid="work-desc-input" rows={2} value={form.desc} onChange={set("desc")} placeholder="Short description shown on hover" className={`${inputCls} resize-none`} />
          {!editing && (
            <input
              ref={fileRef}
              data-testid="work-file-input"
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-[#A6A6B0] file:mr-4 file:rounded-md file:border-0 file:bg-[#6D00FF]/20 file:text-[#E8B6FF] file:px-4 file:py-2.5 file:text-xs file:font-bold file:uppercase file:tracking-[0.14em] hover:file:bg-[#6D00FF]/30 file:cursor-pointer"
            />
          )}
          <button
            data-testid="work-save-button"
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-[#6D00FF] hover:bg-[#8B2CFF] disabled:opacity-60 text-white text-xs font-bold uppercase tracking-[0.14em] px-6 py-3 rounded-md transition-colors duration-300"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? "Saving…" : editing ? "Update Project" : "Add Project"}
          </button>
        </form>
      )}

      {pieces.length === 0 ? (
        <div className="glass-card rounded-lg p-16 text-center">
          <p className="text-[#A6A6B0] text-sm">No projects yet. Add your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="work-list">
          {pieces.map((p, i) => (
            <div key={p.id} data-testid={`work-item-${i}`} className="group relative glass-card rounded-lg overflow-hidden">
              <img src={fullSrc(p.src)} alt={p.name} loading="lazy" className="w-full aspect-[16/11] object-cover" />
              <div className="p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C43CFF]">{p.category}</p>
                <p className="text-sm font-semibold mt-1">{p.name}</p>
              </div>
              <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                <button
                  data-testid={`work-edit-${i}`}
                  onClick={() => openEdit(p)}
                  className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white border border-white/30 rounded-md px-4 py-2 hover:bg-white/10 transition-colors duration-300"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  data-testid={`work-delete-${i}`}
                  onClick={() => remove(p.id)}
                  className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#C43CFF] border border-[#C43CFF]/40 rounded-md px-4 py-2 hover:bg-[#C43CFF]/10 transition-colors duration-300"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
